/**
 * Everything the save editor needs out of the player's own install, resolved
 * once and held.
 *
 * The three consumers all want the same thing — the install as the *engine*
 * would see it, loose `data/` shadowing patch000.dat shadowing master.dat — and
 * all three used to be missing it:
 *
 *   - the Globals tab, which showed `GVAR 412` on every DAT-only install
 *     because it only ever looked for a loose `data/vault13.gam`;
 *   - the Inventory tab's catalog, which needs `items.lst`, the `.pro` files
 *     and `pro_item.msg`;
 *   - the current-inventory list, which needs the protos to know how long each
 *     record in the save's opaque span is.
 *
 * Loading is deliberately one promise for all of it. On a stock install the
 * catalog alone is ~540 inflates out of master.dat, and doing that once per tab
 * would be visible.
 *
 * Nothing here throws. An install we cannot read yields an `InstallData` whose
 * `globals` is null and whose catalog is empty, and every caller already has a
 * fallback for that — numeric GVARs, and an Inventory tab that says the catalog
 * could not be read.
 */

import { activeGameType, cfgFileNameFor, homeDir } from '../config';
import type { EmFS } from '../engine';
import { parseGamGlobals, type GamGlobals } from '../saveedit/gvars';
import { gameFiles, type GameFiles } from './files';
import { ArtResolver } from './frm';
import { loadItemCatalog, type ItemCatalog } from './items';
import { loadCritterArt, type CritterArt } from './critterart';
import { EMPTY_CHARGEN_TEXT, loadChargenText, type ChargenText } from './chargen';

export type { GameFiles } from './files';
export type { CatalogItem, DrugProtoData, ItemCatalog } from './items';
export type { ChargenEntry, ChargenText } from './chargen';
export type { CritterArt } from './critterart';
export { ArtResolver } from './frm';

export interface InstallData {
  files: GameFiles;
  /** The player's own GVAR name table, or null when `vault13.gam` is missing. */
  globals: GamGlobals | null;
  /** Where `vault13.gam` came from — `data/`, `patch000.dat`, `master.dat`. */
  globalsSource: string | null;
  catalog: ItemCatalog;
  /**
   * Perk/trait/skill/stat names, descriptions and icons out of the install's own
   * `.msg` files. Empty — never invented — when the install has no text.
   */
  chargen: ChargenText;
  art: ArtResolver;
  /**
   * Critter art bases and the critter `.frm` inventory, for the wield test in
   * `saveedit/wieldable.ts`. `ok` is false on an install whose archives carry
   * no critter art, and the Inventory tab then simply does not offer the badge.
   */
  critterArt: CritterArt;
}

const decodeLatin1 = (bytes: Uint8Array): string => {
  let s = '';
  for (let i = 0; i < bytes.length; i += 8192) {
    s += String.fromCharCode(...bytes.subarray(i, Math.min(i + 8192, bytes.length)));
  }
  return s;
};

/** `[system] language` from the running engine's config, when it says. */
function configuredLanguage(FS: EmFS): string | null {
  try {
    const game = activeGameType();
    const cfg = FS.readFile(`${homeDir(game)}/${cfgFileNameFor(game)}`, { encoding: 'utf8' }) as string;
    const m = /^\s*language\s*=\s*(\S+)\s*$/im.exec(cfg);
    return m ? m[1]! : null;
  } catch {
    return null;
  }
}

/**
 * The path the engine itself asks for (game.cc:1043). Note the `data\` prefix:
 * inside master.dat that really is a member called `data\vault13.gam`, and on a
 * folder install it is the working-directory fallback in `files.ts` that finds
 * `<install>/data/vault13.gam`. Both are the same request.
 */
const GAM_PATH = 'data/vault13.gam';

async function build(FS: EmFS): Promise<InstallData> {
  const files = await gameFiles(FS);
  const language = configuredLanguage(FS);

  // Provenance is reported because "names came from master.dat" and "names came
  // from your own modded data/" are different claims, and a player's mods live
  // in the difference.
  const gam = await files.readFrom(GAM_PATH);
  const globals = gam ? parseGamGlobals(decodeLatin1(gam.bytes)) : null;
  const globalsSource = globals?.vars.length ? gam!.source : null;

  // Both reads walk the same archives, and the Perks tab is as likely to be the
  // first one opened as the Inventory tab, so they go together rather than
  // one-then-the-other.
  const [catalog, chargen, critterArt] = await Promise.all([
    loadItemCatalog(files, language),
    loadChargenText(files, language),
    loadCritterArt(files),
  ]);

  return {
    files,
    globals: globals?.vars.length ? globals : null,
    globalsSource,
    catalog,
    chargen,
    art: new ArtResolver(files),
    critterArt,
  };
}

/** What a wholly unreadable install yields: an answer of "do not ask". */
const EMPTY_CRITTER_ART: CritterArt = {
  ok: false,
  hasArt: () => false,
  artBase: async () => null,
  resolvedArtBase: () => null,
};

let cached: { fs: EmFS; data: Promise<InstallData> } | null = null;

/** The install behind this filesystem, memoised. Never rejects. */
export function installData(FS: EmFS): Promise<InstallData> {
  if (cached && cached.fs === FS) return cached.data;
  const data = build(FS).catch(async () => {
    const files = await gameFiles(FS);
    return {
      files,
      globals: null,
      globalsSource: null,
      catalog: { items: [], byPid: new Map(), ok: false, textPath: null },
      chargen: EMPTY_CHARGEN_TEXT,
      art: new ArtResolver(files),
      critterArt: await loadCritterArt(files).catch(() => EMPTY_CRITTER_ART),
    } satisfies InstallData;
  });
  cached = { fs: FS, data };
  return data;
}

/** Drops the memo. Only the tests need this. */
export function resetInstallDataCache(): void {
  cached = null;
}
